import React from 'react'
import { Document, Image, Page, Text, View } from '@react-pdf/renderer'
import moment from 'moment'
import RecordSection from '../../../patientRecords/Sharing/PDF/_RecordSection'
import InfoLine from '../../../patientRecords/Sharing/PDF/_InfoLine'

// Helper: render a single checklist item line
const ChecklistItem = ({ label, answer }) => {
  const { safe, notApplicable, newPartFitted, advised, comments } = answer || {}

  // Build status string(s)
  const statusParts = []
  if (safe) statusParts.push('SAFE')
  if (notApplicable) statusParts.push('N/A')
  if (newPartFitted) statusParts.push('PART FITTED')
  if (advised) statusParts.push('ADVISORY')

  const statusText = statusParts.length
    ? statusParts.join(' · ')
    : 'NOT RECORDED'

  return (
    <View
      style={{
        marginVertical: 3,
        padding: 5,
        borderStyle: 'solid',
        borderWidth: 0.5,
        borderRadius: 2,
      }}
    >
      <View
        style={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          fontSize: 9,
        }}
      >
        <Text style={{ fontFamily: 'Helvetica-Bold', width: '60%' }}>
          {label}
        </Text>
        <Text
          style={{
            fontSize: 8,
            textAlign: 'right',
            width: '40%',
          }}
        >
          {statusText}
        </Text>
      </View>

      {comments && comments.trim() !== '' && (
        <Text
          style={{
            fontSize: 8,
            marginTop: 2,
            color: '#555',
          }}
        >
          {comments}
        </Text>
      )}
    </View>
  )
}

const VehicleSafetyCheckPDF = ({ report, orgSettings, printedBy }) => {
  if (!report) return null

  const { vehicle, testedByEmployee, testedBy, testStats, answers } = report
  const stats = testStats || {}

  const DocumentComponent = () => (
    <Document options={{ isEvalSupported: false }}>
      <Page
        size="A4"
        style={{
          paddingVertical: 40,
          paddingHorizontal: 20,
          fontFamily: 'Helvetica',
        }}
      >
        {/* HEADER */}
        <View
          style={{
            flexDirection: 'column',
            gap: 4,
            paddingBottom: 10,
          }}
          wrap={true}
        >
          <View
            style={{
              flexDirection: 'row',
              justifyContent: 'space-between',
              paddingHorizontal: 20,
              alignItems: 'center',
            }}
          >
            {orgSettings?.logo && (
              <Image src={orgSettings.logo} style={{ height: 30 }} />
            )}
            <Text style={{ textAlign: 'right', fontSize: 14, color: 'grey' }}>
              {orgSettings?.orgName || 'AmbuNet Client Organisation'}
            </Text>
          </View>

          <View
            style={{
              flexDirection: 'column',
              alignItems: 'center',
              marginTop: 14,
              gap: 4,
            }}
          >
            <Text style={{ fontSize: 18, color: '#3c4e50' }}>
              Vehicle Safety Check Report
            </Text>
            <Text style={{ fontSize: 12, color: '#5f047bff' }}>
              {vehicle?.callSign ? `Vehicle: ${vehicle.callSign}` : ''}
            </Text>
            <Text style={{ fontSize: 10, color: 'grey' }}>
              Completed on{' '}
              {report.testDate
                ? moment(report.testDate).format('Do MMMM YYYY')
                : '—'}
            </Text>
          </View>

          {/* CONFIDENTIALITY BLURB */}
          <View
            style={{
              marginVertical: 10,
              marginHorizontal: 20,
              padding: 10,
              borderStyle: 'solid',
              borderWidth: 1,
            }}
          >
            <Text style={{ fontSize: 8 }}>
              This document records the outcome of a vehicle safety and
              roadworthiness check. It may include sensitive operational
              information. It is intended for use by authorised personnel only,
              for the purposes of fleet management, compliance, and governance.
              Unauthorised access, copying, or distribution is strictly
              prohibited. If you are not the intended recipient, you must notify
              the sender and securely delete this document.
            </Text>
          </View>

          {/* INSPECTION DETAILS */}
          <RecordSection title="Inspection Details">
            <InfoLine
              label="Vehicle"
              value={vehicle?.callSign || vehicle?._id || '—'}
            />
            <InfoLine
              label="Inspection Date"
              value={
                report.testDate
                  ? moment(report.testDate).format('Do MMMM YYYY [-] HH:mm')
                  : '—'
              }
            />
            <InfoLine label="Mileage" value={report.mileage || '—'} />

            {testedByEmployee ? (
              <>
                <InfoLine
                  label="Technician"
                  value={
                    testedByEmployee?.userAccount
                      ? `${testedByEmployee.userAccount.firstName} ${testedByEmployee.userAccount.lastName}`
                      : '—'
                  }
                />
                <InfoLine label="Inspection Type" value="Internal (Employee)" />
              </>
            ) : (
              <>
                <InfoLine
                  label="Garage Name"
                  value={testedBy?.garageName || '—'}
                />
                <InfoLine
                  label="Technician"
                  value={testedBy?.technicianName || '—'}
                />
                <InfoLine
                  label="Invoice Number"
                  value={testedBy?.invoiceNumber || '—'}
                />
                <InfoLine label="Inspection Type" value="External Garage" />
              </>
            )}

            {printedBy && (
              <InfoLine
                label="Printed By"
                value={
                  printedBy.firstName
                    ? `${printedBy.firstName} ${printedBy.lastName}`
                    : printedBy
                }
              />
            )}
          </RecordSection>

          {/* SUMMARY STATS */}
          <RecordSection title="Summary">
            <InfoLine
              label="Total Checks Completed"
              value={stats.totalAnswered?.toString() ?? '0'}
            />
            <InfoLine label="Safe" value={stats.safeCount?.toString() ?? '0'} />
            <InfoLine label="N/A" value={stats.naCount?.toString() ?? '0'} />
            <InfoLine
              label="Parts Fitted"
              value={stats.partCount?.toString() ?? '0'}
            />
            <InfoLine
              label="Advisories"
              value={stats.advisoryCount?.toString() ?? '0'}
            />
          </RecordSection>

          {/* CHECKLIST BY CATEGORY */}
          {answers &&
            Object.keys(answers).map((category) => (
              <RecordSection key={category} title={category}>
                <View
                  style={{
                    flexDirection: 'column',
                    fontSize: 9,
                    paddingHorizontal: 5,
                    paddingVertical: 2,
                    width: '100%',
                  }}
                >
                  {Object.keys(answers[category] || {}).map((itemKey) => (
                    <ChecklistItem
                      key={itemKey}
                      label={itemKey}
                      answer={answers[category][itemKey]}
                    />
                  ))}
                </View>
              </RecordSection>
            ))}

          {/* ADDITIONAL COMMENTS */}
          {report.additionalComments && (
            <RecordSection title="Additional Comments">
              <InfoLine
                label=""
                value={report.additionalComments || ''}
                fullWidth
              />
            </RecordSection>
          )}
        </View>

        {/* FOOTER */}
        <View
          style={{
            position: 'absolute',
            bottom: 40,
            left: 20,
            right: 20,
            fontSize: 10,
            color: 'grey',
          }}
          render={({ pageNumber, totalPages }) =>
            pageNumber === totalPages && (
              <View style={{ flexDirection: 'column', gap: 2 }}>
                <Text style={{ fontFamily: 'Helvetica-Bold', fontSize: 10 }}>
                  Powered by AmbuNet &#174;
                </Text>
                <Text style={{ fontSize: 9 }}>
                  AmbuNet® provides the technical platform for recording and
                  generating this vehicle safety check. AmbuNet® is not
                  responsible for the accuracy or completeness of the
                  information entered, nor for any decisions or actions taken as
                  a result. Responsibility for the lawful and appropriate use of
                  this report rests with the submitting user and the issuing
                  organisation.
                </Text>
              </View>
            )
          }
        />
      </Page>
    </Document>
  )

  return <DocumentComponent />
}

export default VehicleSafetyCheckPDF
